import { useEffect, useState } from 'react';
import { dynoapeAPI } from '@api/dynoape';
import { useDepartment } from '@hooks';

export interface LicenseInfo {
    employeesInDepartment: number;
    remainingEmployees: number;
    generateTokens: number;
    remainingGenerateTokens: number;
}

export interface UseLicenseInfoResult {
    license: LicenseInfo | undefined;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export const useLicenseInfo = (refresh = 0): UseLicenseInfoResult => {
    const { departmentId } = useDepartment();
    const [license, setLicense] = useState<LicenseInfo | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchLicense = async (): Promise<void> => {
        if (!departmentId) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const data = await dynoapeAPI.get<LicenseInfo>(
                `/api/v1/department/${departmentId}/active-license`,
            );
            setLicense(data);
        } catch (err) {
            setError(
                err instanceof Error
                    ? err
                    : new Error('Failed to fetch license'),
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLicense();
    }, [departmentId, refresh]);

    return {
        license,
        isLoading,
        error,
        refetch: fetchLicense,
    };
};

export default useLicenseInfo;
